import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { createScopedJSONStorage, scopedLocalStorage } from '@/lib/browser/scopedStorage';

const STORAGE_KEY = 'dataPackageFilters';
export const COLUMN_VISIBILITY_STORAGE_KEY = 'dataPackageColumnVisibility';

/** Default filter criteria for data package search */
export const defaultFilterCriteria = {
  searchText: '',
  dataType: '',
  synTechniqueId: '',
  charTechniqueId: '',
  elements: [] as string[],
  materials: [] as number[],
  topLevelOrPublicOnly: true,
  publicOnly: false,
};

export const defaultColumnVisibilityModel: GridColumnVisibilityModel = {
  label: true,
  doi: true,
  projectLabel: true,
  publicationDoi: true,
  availableData: true,
  type: true,
  lastChangedDate: true,
};

export function loadStoredColumnVisibility(): GridColumnVisibilityModel {
  if (typeof window === 'undefined') return defaultColumnVisibilityModel;

  try {
    const stored = scopedLocalStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    if (!stored) return defaultColumnVisibilityModel;
    return JSON.parse(stored) as GridColumnVisibilityModel;
  } catch {
    return defaultColumnVisibilityModel;
  }
}

interface DataPackageFilterStore {
  /** Current filter criteria for data package search */
  filters: typeof defaultFilterCriteria;
  /** True while the data package grid is actively fetching results */
  isSearching: boolean;
  /** Sets search-in-progress state (not persisted) */
  setIsSearching: (isSearching: boolean) => void;
  /** Updates the filter criteria and persists to localStorage */
  setFilters: (filters: typeof defaultFilterCriteria) => void;
  /** Resets filters to default criteria and removes from localStorage */
  resetFilters: () => void;
  /** Current pagination state */
  paginationModel: { page: number; pageSize: number };
  /** Updates pagination state, accepts either new state or updater function */
  setPaginationModel: (
    model:
      | { page: number; pageSize: number }
      | ((prev: { page: number; pageSize: number }) => { page: number; pageSize: number })
  ) => void;
}

/**
 * Zustand store for managing data package filters and pagination
 * Uses Zustand's persist middleware for automatic localStorage persistence.
 */
export const useDataPackageFilterStore = create<DataPackageFilterStore>()(
  persist(
    (set, get) => ({
      filters: defaultFilterCriteria,
      isSearching: false,
      setIsSearching: (isSearching) => set({ isSearching }),
      setFilters: (filters) => set({ filters }),
      resetFilters: () => set({ filters: defaultFilterCriteria }),
      paginationModel: { page: 0, pageSize: 25 },
      setPaginationModel: (modelOrUpdater) => {
        const current = get().paginationModel;
        const newModel = typeof modelOrUpdater === 'function' ? modelOrUpdater(current) : modelOrUpdater;
        set({ paginationModel: newModel });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createScopedJSONStorage(),
      partialize: (state) => ({
        filters: state.filters,
        paginationModel: state.paginationModel,
      }),
    }
  )
);
